/**
 * PauseForm — create/edit/delete a pause for a contract.
 * Opens as a Dialog. Surfaces 422 overlap errors inline.
 *
 * Props:
 *   open        {boolean}
 *   onClose     {()=>void}
 *   contractId  {number}
 *   pause       {Object|null}  null = create mode
 *   onSuccess   {()=>void}
 *   showToast   {({message,variant})=>void}
 */

import { useState, useEffect } from 'react';
import Dialog from '../ui/Dialog';
import Button from '../ui/Button';
import Field from '../ui/Field';
import { getFieldErrors, getUserMessage } from '../../utils/apiErrors';
import apiService from '../../services/apiService';

const INIT = {
  start_date: '',
  end_date: '',
  reason: '',
};

const PauseForm = ({ open, onClose, contractId, pause, onSuccess, showToast }) => {
  const isEdit = Boolean(pause);
  const [form, setForm] = useState(INIT);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(
        pause
          ? {
              start_date: pause.start_date ?? '',
              end_date: pause.end_date ?? '',
              reason: pause.reason ?? '',
            }
          : INIT
      );
      setErrors({});
    }
  }, [open, pause]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.start_date) e.start_date = 'Date de début requise.';
    if (!form.end_date) e.end_date = 'Date de fin requise.';
    if (form.start_date && form.end_date && form.start_date > form.end_date) {
      e.end_date = 'La date de fin doit être après la date de début.';
    }
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        start_date: form.start_date,
        end_date: form.end_date,
        ...(form.reason.trim() ? { reason: form.reason.trim() } : {}),
      };
      if (isEdit) {
        await apiService.updatePause(contractId, pause.id, payload);
        showToast({ message: 'Pause mise à jour.', variant: 'success' });
      } else {
        await apiService.createPause(contractId, payload);
        showToast({ message: 'Pause créée.', variant: 'success' });
      }
      onSuccess();
      onClose();
    } catch (err) {
      const fieldErrors = getFieldErrors(err);
      if (Object.keys(fieldErrors).length) {
        setErrors(fieldErrors);
      } else {
        showToast({ message: getUserMessage(err), variant: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setSubmitting(true);
    try {
      await apiService.deletePause(contractId, pause.id);
      showToast({ message: 'Pause supprimée.', variant: 'success' });
      setDeleteConfirmOpen(false);
      onSuccess();
      onClose();
    } catch (err) {
      showToast({ message: getUserMessage(err), variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog
        open={open && !deleteConfirmOpen}
        onClose={onClose}
        title={isEdit ? 'Modifier la pause' : 'Nouvelle pause'}
      >
        <div className="space-y-4">
          <Field
            label="Date de début *"
            id="pause-start"
            name="start_date"
            type="date"
            value={form.start_date}
            onChange={(e) => handleChange('start_date', e.target.value)}
            error={errors.start_date}
            disabled={submitting}
          />

          <Field
            label="Date de fin *"
            id="pause-end"
            name="end_date"
            type="date"
            value={form.end_date}
            onChange={(e) => handleChange('end_date', e.target.value)}
            error={errors.end_date}
            disabled={submitting}
          />

          <Field
            label="Motif (optionnel)"
            id="pause-reason"
            name="reason"
            value={form.reason}
            onChange={(e) => handleChange('reason', e.target.value)}
            error={errors.reason}
            placeholder="Ex: Vacances scolaires…"
            disabled={submitting}
          />

          {/* Generic overlap or API error */}
          {errors.overlap && (
            <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
              {errors.overlap}
            </p>
          )}

          <div className="flex items-center gap-2 justify-between pt-2 border-t border-gray-100">
            {isEdit && (
              <Button
                variant="danger"
                size="sm"
                disabled={submitting}
                onClick={() => setDeleteConfirmOpen(true)}
              >
                Supprimer
              </Button>
            )}
            <div className="flex gap-2 ml-auto">
              <Button
                variant="ghost"
                size="sm"
                disabled={submitting}
                onClick={onClose}
              >
                Annuler
              </Button>
              <Button
                variant="primary"
                size="sm"
                disabled={submitting}
                onClick={handleSubmit}
              >
                {submitting ? 'Enregistrement…' : isEdit ? 'Modifier' : 'Créer'}
              </Button>
            </div>
          </div>
        </div>
      </Dialog>

      {/* Delete confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        title="Supprimer la pause"
      >
        <div className="space-y-4">
          <p className="text-sm font-montserrat text-[#4A4A4A]">
            Êtes-vous sûr de vouloir supprimer cette pause ?
          </p>
          <div className="flex gap-2 justify-end">
            <Button
              variant="ghost"
              size="sm"
              disabled={submitting}
              onClick={() => setDeleteConfirmOpen(false)}
            >
              Retour
            </Button>
            <Button
              variant="danger"
              size="sm"
              disabled={submitting}
              onClick={handleDelete}
            >
              {submitting ? 'Suppression…' : 'Supprimer'}
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default PauseForm;
